import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

export interface CommunityImage {
  id: string;
  label: string;
  file_name: string;
  category: string;
  tags: string[];
  source: string;
}

interface CommunityImagePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (image: { fileName: string; label: string; url: string }) => void;
  initialSearch?: string;
  allowedCategories?: string[];
}

export function CommunityImagePicker({
  open,
  onOpenChange,
  onSelect,
  initialSearch = "",
  allowedCategories,
}: CommunityImagePickerProps) {
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (open && initialSearch) {
      setSearchTerm(initialSearch);
      setDebouncedSearch(initialSearch);
    }
  }, [open, initialSearch]);

  const params = new URLSearchParams();
  if (debouncedSearch) params.set("search", debouncedSearch);
  if (allowedCategories?.length === 1) params.set("category", allowedCategories[0]);
  const queryString = params.toString();
  const queryUrl = `/api/community-images${queryString ? `?${queryString}` : ""}`;

  const { data: rawImages = [], isLoading } = useQuery<CommunityImage[]>({
    queryKey: ["/api/community-images", debouncedSearch, allowedCategories],
    queryFn: async () => {
      const res = await fetch(queryUrl, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load images");
      return res.json();
    },
    enabled: open,
  });

  const images = allowedCategories && allowedCategories.length > 1
    ? rawImages.filter((img) => allowedCategories.includes(img.category))
    : rawImages;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Image Library</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search images..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              Loading images...
            </div>
          ) : images.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              {debouncedSearch ? "No images found" : "No images available"}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 p-1">
              {images.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => {
                    onSelect({
                      fileName: img.file_name,
                      label: img.label,
                      url: `/community/openmoji/${img.file_name}`,
                    });
                    onOpenChange(false);
                  }}
                  className="flex flex-col items-center gap-1 p-2 rounded-lg border border-transparent hover:border-primary hover:bg-primary/5 transition cursor-pointer"
                >
                  <div className="w-14 h-14 flex items-center justify-center">
                    <img
                      src={`/community/openmoji/${img.file_name}`}
                      alt={img.label}
                      className="w-12 h-12"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-xs text-center text-muted-foreground truncate w-full">
                    {img.label}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
